import React, { useMemo, useState } from 'react';
import { Box, Button, Paper, TextField, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../api';
import { validatePassword } from '../../helpers/validate_password';

const fieldSx = {
    backgroundColor: '#f9f9f9',
    '& .MuiInputLabel-root': { color: '#666666' },
    '& .MuiOutlinedInput-root': {
        color: '#111111',
        '& fieldset': { borderColor: '#cccccc' },
        '&:hover fieldset': { borderColor: '#999999' },
    },
};

const primaryButtonSx = (isSubmitting) => ({
    marginTop: 2,
    background: isSubmitting
        ? 'linear-gradient(to right, #6c757d, #495057)'
        : 'linear-gradient(to right, #007bff, #0056b3)',
    color: '#fff',
    fontWeight: 'bold',
    borderRadius: '8px',
    padding: '12px 0',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
    '&:hover': {
        background: isSubmitting
            ? 'linear-gradient(to right, #6c757d, #495057)'
            : 'linear-gradient(to right, #0056b3, #003d7a)',
    },
});

function PasswordRedefinition() {
    const location = useLocation();
    const navigate = useNavigate();
    const resetPasswordToken = useMemo(
        () => new URLSearchParams(location.search).get('reset_password_token'),
        [location.search],
    );

    const [password, setPassword] = useState('');
    const [passwordConfirmation, setPasswordConfirmation] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [confirmationError, setConfirmationError] = useState('');
    const [formError, setFormError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const hasValidToken = Boolean(resetPasswordToken?.trim());

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError('');
        setSuccessMessage('');

        const validation = validatePassword(password);
        if (!validation.isValid) {
            setPasswordError(validation.message.replace(/<br>/g, '\n').replace(/<br \/>/g, '\n'));
            return;
        }
        setPasswordError('');

        if (password !== passwordConfirmation) {
            setConfirmationError('As senhas não correspondem.');
            return;
        }
        setConfirmationError('');

        setIsSubmitting(true);
        try {
            const response = await api.post(
                `users/redefine-password?reset_password_token=${encodeURIComponent(resetPasswordToken)}`,
                { password },
            );
            setSuccessMessage(
                response.data?.message || 'Senha redefinida com sucesso! Você já pode fazer login com a nova senha.',
            );
        } catch (err) {
            const message =
                err.response?.data?.message ||
                err.response?.data?.error ||
                'Não foi possível redefinir sua senha. O link pode ter expirado.';
            setFormError(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                px: 2,
                py: 4,
                backgroundImage: 'url(/images/3D_banner_1.webp)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                position: 'relative',
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                }}
            />
            <Paper
                elevation={6}
                sx={{
                    position: 'relative',
                    zIndex: 1,
                    width: '100%',
                    maxWidth: 480,
                    p: { xs: 3, sm: 4 },
                    backgroundColor: '#ffffff',
                    color: '#111111',
                    borderRadius: '12px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.25)',
                }}
            >
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <Box
                        component="img"
                        src="/images/logo-acompanha-transp.png"
                        alt="Acompanha 3D"
                        sx={{ width: 160, height: 'auto', mb: 2 }}
                    />
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a', mb: 1 }}>
                        Redefinir senha
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#475569', lineHeight: 1.6 }}>
                        {hasValidToken
                            ? 'Escolha uma nova senha segura para acessar sua conta.'
                            : 'Este link de redefinição é inválido ou expirou.'}
                    </Typography>
                </Box>

                {!hasValidToken ? (
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography color="error" variant="body2" sx={{ mb: 3 }}>
                            Solicite um novo link em &quot;Esqueci minha senha&quot; na tela de login.
                        </Typography>
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={() => navigate('/admin/forgot-password')}
                            sx={primaryButtonSx(false)}
                        >
                            Solicitar novo link
                        </Button>
                        <Button
                            fullWidth
                            onClick={() => navigate('/admin/login')}
                            sx={{
                                mt: 2,
                                color: '#007bff',
                                fontWeight: 600,
                                textDecoration: 'underline',
                            }}
                        >
                            Voltar ao login
                        </Button>
                    </Box>
                ) : successMessage ? (
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography color="success.main" variant="body1" sx={{ mb: 3, lineHeight: 1.65 }}>
                            {successMessage}
                        </Typography>
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={() => navigate('/admin/login')}
                            sx={primaryButtonSx(false)}
                        >
                            Ir para o login
                        </Button>
                    </Box>
                ) : (
                    <Box component="form" onSubmit={handleSubmit} noValidate>
                        <TextField
                            label="Nova senha"
                            type="password"
                            variant="outlined"
                            fullWidth
                            margin="normal"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            onBlur={() => {
                                if (!password) return;
                                const validation = validatePassword(password);
                                setPasswordError(
                                    validation.isValid
                                        ? ''
                                        : validation.message.replace(/<br>/g, '\n').replace(/<br \/>/g, '\n'),
                                );
                            }}
                            required
                            autoComplete="new-password"
                            sx={fieldSx}
                        />
                        {passwordError && (
                            <Typography
                                color="error"
                                variant="caption"
                                component="div"
                                sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}
                            >
                                {passwordError}
                            </Typography>
                        )}

                        <TextField
                            label="Confirme a nova senha"
                            type="password"
                            variant="outlined"
                            fullWidth
                            margin="normal"
                            value={passwordConfirmation}
                            onChange={(e) => setPasswordConfirmation(e.target.value)}
                            onBlur={() => {
                                if (passwordConfirmation && password !== passwordConfirmation) {
                                    setConfirmationError('As senhas não correspondem.');
                                } else {
                                    setConfirmationError('');
                                }
                            }}
                            onPaste={(e) => e.preventDefault()}
                            required
                            autoComplete="new-password"
                            sx={fieldSx}
                        />
                        {confirmationError && (
                            <Typography color="error" variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                                {confirmationError}
                            </Typography>
                        )}

                        {formError && (
                            <Typography color="error" variant="body2" sx={{ mt: 2 }}>
                                {formError}
                            </Typography>
                        )}

                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disabled={isSubmitting}
                            sx={primaryButtonSx(isSubmitting)}
                        >
                            {isSubmitting ? 'Salvando...' : 'Redefinir senha'}
                        </Button>

                        <Button
                            fullWidth
                            onClick={() => navigate('/admin/login')}
                            sx={{
                                mt: 2,
                                color: '#007bff',
                                fontWeight: 600,
                                textDecoration: 'underline',
                                '&:hover': { backgroundColor: 'transparent' },
                            }}
                        >
                            Voltar ao login
                        </Button>
                    </Box>
                )}
            </Paper>
        </Box>
    );
}

export default PasswordRedefinition;
